import { apiDownload, apiRequest } from '@/shared/api/client';
import type { Envelope } from '@/shared/types/api';
import type { RecordDataset, ReportCatalogue, ReportPayload } from '@/shared/types/domain';

/*
 * RPT-01…RPT-08, RPT-11 — the reporting hub.
 *
 * Every call is a READ. Reporting aggregates, filters and derives; it never
 * becomes the system of record, so there is no POST here and there will not be
 * one.
 */

/** The dimensions R-032 §9 names, as query parameters. */
export interface ReportQuery {
  from?: string;
  to?: string;
  branch_id?: string;
  region_id?: string;
  category_id?: string;
  supplier_id?: string;
  group_by?: string;
  budget_period_id?: string;
  page?: number;
  open?: number;
  late?: number;
  /** Still open and past its due time or deadline (approvals, enquiries). */
  overdue?: number;
  /** Orders that were issued: a draft was never part of what was ordered or committed. */
  issued?: number;
  /** Orders other than cancelled ones: what "Ordered" adds up leaves them out. */
  exclude_cancelled?: number;
  /** Enquiries awarded in the period (RFQ Analytics). */
  awarded?: number;
  /** Enquiries closed without an award in the period (RFQ Analytics). */
  no_award?: number;
  /**
   * Enquiries opened and decided in the period, whatever the outcome: the ones
   * the RFQ cycle time averages (DEF-017).
   */
  decided?: number;
  /** One currency's records: money is never added, or listed, across currencies. */
  currency?: string;
  /**
   * The part of a Spend invoiced figure a list of supplier invoices was opened
   * for (DEF-030): `attributed` or `unattributed`.
   */
  attribution?: string;
}

export const getReportCatalogue = () => apiRequest<Envelope<ReportCatalogue>>('/reports');

export const getReport = (id: string, query: ReportQuery = {}) =>
  apiRequest<Envelope<ReportPayload>>(`/reports/${id}`, { query: { ...query } });

/** FLW-25 — the records an aggregate figure was counted from. */
export const getRecords = (dataset: string, query: ReportQuery = {}) =>
  apiRequest<
    Envelope<RecordDataset> & {
      meta: { current_page: number; last_page: number; per_page: number; total: number };
    }
  >(`/reports/records/${dataset}`, { query: { ...query } });

export const downloadReport = (id: string, format: string, query: ReportQuery, locale: string) =>
  apiDownload(`/reports/${id}/export`, {
    query: { ...query, format },
    locale,
    fallbackName: `fidelen-${id}.${format}`,
  });

export const downloadRecords = (
  dataset: string,
  format: string,
  query: ReportQuery,
  locale: string,
) =>
  apiDownload(`/reports/records/${dataset}/export`, {
    query: { ...query, format },
    locale,
    fallbackName: `fidelen-${dataset}.${format}`,
  });

/** RPT-12 — the audit trail, under the same filters as the audit screen. */
export const downloadAudit = (
  format: string,
  query: Record<string, string | undefined>,
  locale: string,
) =>
  apiDownload('/audit/export', {
    query: { ...query, format },
    locale,
    fallbackName: `fidelen-audit.${format}`,
  });
